import { NamePath } from 'antd/es/form/interface';
import {
  ICulture,
  ICultureVariation,
} from 'app/containers/Cultures/slice/types';
import { IGpsTransport } from 'app/containers/Garage/types';
import {
  FieldsBoundaries,
  IFieldFeature,
} from 'app/containers/Map/slice/types';
import { IRecipe } from 'app/containers/Recipe/types';
import { ISimpleUser } from 'app/containers/User/slice/types';
import { Moment } from 'moment';
import { IPaginated } from 'types';
import { ICompanyInList } from '../../Company/slice/types';

/* --- STATE --- */
export interface TaskState {
  taskList: ITaskListState;
  singleFieldTaskList: ISingleFieldTaskListState;
  categories: {
    loading: boolean;
    categoriesArr: ITaskCategory[];
  };
  createTask: {
    loading: boolean;
  };
  taskDetails: {
    loading: boolean;
    task: ITask | null;
    notes: string;

    recipe: IRecipe | null;
  };
  taskResources: {
    loading: boolean;
    plannedResources: {
      resourcesArr: ITaskPlannedResource[];
      updatedResourcesArr: ITaskPlannedResource[];
    };

    thisStageUsedResourcesArr: ITaskUsedResource[];
    usedResources: {
      resourcesArr: ITaskUsedResource[];
      updatedResourcesArr: ITaskUsedResource[];
    };

    availableResourcesArr: ITaskAvailableResourceOption[];
  };
  taskProgress: {
    loading: boolean;
    task: ITask | null;
    notes: string;
  };
  taskProgressUpdateLoading: boolean;

  routesWithoutTask: IRoutesWithoutTaskState;
  routesWithoutTaskFilters: IRoutesWithoutTaskFilters;

  checkedRouteIds: number[];
}

export interface ITaskListState {
  loading: boolean;
  tasksArr: ITasksTableItem[];
  filters: ITaskFilters;
  currentPage: number;
  total: number;
  sort: TaskListSort;
}

export type TaskListSort = {
  name: string;
  order: 'asc' | 'desc';
} | null;

export interface ISingleFieldTaskListState extends ITaskListState {
  fieldId: number | null;
  allFieldTasksArr: ITasksTableItem[];
}

export interface ITask {
  id: number;
  // TODO: remove from endpoint
  hiikoEnvironment: unknown;

  category: ITaskCategory;
  name: string;
  createdBy: ISimpleUser;
  updatedBy: ISimpleUser;
  createdAt: string;
  updatedAt: string;
  startDate: string;
  endDate: string;
  fieldTasks: IFieldTask[] | null;

  inProgressArea: number;
  plannedArea: number;
  completedArea: number;
  totalArea: number;

  recipe?: IRecipe | null;
  recommendation?: ITaskRecommendation | null;
  note: string | null;
  taskReferenceId: null | number;
  status: TaskStatusType;
}

export interface ITaskCategory {
  id: number;
  name: string;
  producesYield: boolean;
}

export interface IAreaByCompanyListItem {
  companyId: number;
  companyName: string;
  area: number;
  readableArea: string;
}

export interface IAreaByCultureListItem {
  cultureId: number;
  cultureName: string;
  area: number;
  readableArea: string;
}

export enum taskFormValuesEnum {
  name = 'name',
  category = 'taskCategoryId',
  date = 'date',
  notes = 'notes',
}

export interface ITaskFormValues {
  name: string;
  taskCategoryId: string;
  date: Moment[];
  notes?: string;
}

export interface ICreateTask {
  name: string;
  endDate: string;
  fieldSeasons: number[];
  hiikoEnvironmentId: number;
  startDate: string;
  taskCategoryId: number;
  note: string;
}

export interface IEditTask extends ICreateTask {}

export interface ITaskFields {
  fieldSeasonIds: number[];
  removeFieldSeasonIds?: number[];
}

export enum taskListTableColEnum {
  name = 'name',
  category = 'category',
  date = 'date',
  id = 'id',
  endDate = 'endDate',

  area = 'totalArea',
  status = 'status',
  cultures = 'cultures',
}
export interface ITaskFiltersFormValues {
  date?: Moment[];
  categoryId?: number;
  status?: taskStatusEnum;
}

export interface ITaskFilters {
  startDate?: string;
  endDate?: string;
  categoryId?: string;
  status?: TaskStatusType[];
}

export enum taskFilterFormEnum {
  date = 'date',
  categoryId = 'categoryId',
  status = 'status',
}

export enum taskFilterEnum {
  categoryId = 'categoryId',
  status = 'status',
  startDate = 'startDate',
  endDate = 'endDate',
}

export enum detailsViewTypeEnum {
  map = 'map',
  resources = 'resources',
  routes = 'routes',
}

export interface ITabMenuItem<T> {
  title: string;
  icon: JSX.Element;
  key: T;
  id?: string;
}

export enum taskStatusEnum {
  planned = 'PLANNED',
  inProgress = 'IN_PROGRESS',
  completed = 'COMPLETED',
  recommendation = 'RECOMMENDATION',
}

export interface IFieldTask {
  id: number;
  taskId: number;
  fieldSeason: {
    id: number;
    season: {
      id: number;
      year: number;
      startDate: string;
      endDate: string;
      name: string;
    };
    field: string;
    geometry: string;
    culture: ICulture | null;
    area: number;
  };
  progress: number;
  status: taskStatusEnum;
  startDate: string;
  endDate: string;
  createdBy: unknown;
  updatedBy: unknown;
  createdAt: string;
  updatedAt: string;

  gpsRoutes?: RouteData[];
}

export interface ITasksTableItem {
  id: number;
  name: string;
  category: ITaskCategory;
  cultures: string[];
  endDate: string;
  completedArea: number;
  totalArea: number;
  status: TaskStatusType;
}

export interface ICreateTaskPayload {
  taskId?: number;
  envId: number;
  task: ICreateTask;
  onSuccessRedirectTo?: string;
  plannedResources: ITaskPlannedResource[];
}

export interface ICreateTaskRecommendationBody extends ICreateTask {
  note: string;
  recipe: {
    id?: number;
    resources: {
      id: number;
      norm: number;
      price?: number;
    }[];
  };
}

export interface IEditTaskRecommendationBody {
  endDate: string;
  name: string;
  note: string;
  recipe: {
    id?: number;
    resources: {
      id: number;
      norm: number;
      price?: number;
    }[];
  };
  startDate: string;
  taskCategoryId: number;
  taskFieldAddRemoveView: {
    fieldSeasonIds: number[];
    removeFieldSeasonIds: number[];
  };
}

export interface IEditTaskPayload extends ICreateTaskPayload {
  taskId: number;
}
export interface IGetTaskDetailsPayload {
  envId: number;
  taskId: number;
}
export interface IGetTaskProgressPayload extends IGetTaskDetailsPayload {}

export interface IFieldUpdate {
  fieldTaskId: number;
  progress: number;
}

export interface IFieldTaskProgressUpdateViewsListItem {
  startDate: string;
  endDate: string;
  fieldTaskId: number;
  progress: number;
  harvest?: number;
}

export interface IFieldProgress extends IFieldUpdate {
  fieldId: number;
  area: number;
}

export interface IFieldProgressWithProgressArea extends IFieldProgress {
  progressArea: number;
}

export interface ISetFieldProgressPayload {
  fieldId: number;
  progress: number;
}

export interface IUpdateTaskProgressPayload {
  fieldTaskProgressUpdateViews: IFieldTaskProgressUpdateViewsListItem[];
  envId: number;
  taskId: number;
}

export interface IGetTaskListPayload {
  envId: number;
  filters: ITaskFilters;
  companyId: number | null;
  seasonId: number | null;
  fieldId?: number;
  page: number;
  sort: TaskListSort;
  companyGroupId: number | null;
}
export interface IGetFieldAllTaskListPayload {
  envId: number;
  fieldId: number;
  companyId: number | null;
  seasonId: number | null;

  companyGroupId: number | null;
}

export interface IGetTaskListParams {
  category_ids?: string;
  company_id?: number;
  culture_ids?: number;
  end_date?: string;
  season_id?: number;
  start_date?: string;
  status?: string;
  field_season_ids?: number;
  page?: number;
  size?: number;
  sort?: string;

  companyGroupId: number | null;
}

export interface ISetTaskListPayload {
  tasksArr: ITasksTableItem[];
  currentPage: number;
  pageSize: number;
  total: number;
}

export interface ISetFieldTaskListPayload extends ISetTaskListPayload {
  fieldId: number;
}

export interface ISetFieldAllTaskListPayload {
  allFieldTasksArr: ITasksTableItem[];
  fieldId: number;
}

export type TaskStatusType =
  | 'PLANNED'
  | 'IN_PROGRESS'
  | 'COMPLETED'
  | 'RECOMMENDED'
  | 'ACCEPTED'
  | 'DECLINED';

export interface ITasksArrResponseItem {
  id: number;
  category: ITaskCategory;
  name: string;
  createdBy: ISimpleUser;
  updatedBy: ISimpleUser;
  createdAt: string;
  updatedAt: string;
  startDate: string;
  endDate: string;
  fieldTasks: null | IFieldTask[];

  totalArea: number;
  plannedArea: number;
  inProgressArea: number;
  completedArea: number;

  recipe?: IRecipe | null;
  recommendation?: ITaskRecommendation | null;
  note: string | null;
  taskReferenceId: null | number;
  status: TaskStatusType;

  // TODO: remove below properties from endpoint
  hiikoEnvironment: unknown;
}

export interface ITaskRecommendation {
  id: number;
  envId: number;
  status: string;
  comment: null | string;
  companies: ICompanyInList[];
  createdBy: ISimpleUser;
  updatedBy: ISimpleUser;
  createdAt: string;
  updatedAt: string;
}

export interface ITaskListResponse extends IPaginated<ITasksArrResponseItem> {}

export interface IGetTaskPlannedResourcesPayload {
  envId: number;
  taskId: number;
}
export interface IGetTaskUsedResourcesPayload
  extends IGetTaskPlannedResourcesPayload {}

export interface ITaskPlannedResourceResponseItem {
  [key: string]: unknown;
  id: number;
  norm: number;
  resource: {
    [key: string]: unknown;
    cultureVariation: ICultureVariation | null;
    name: string;
    recommendedNorm: number;
    id: number;
    type: {
      id: number;
      name: string;
      unit: string;
      seed: boolean;
    };
  };
  task: {
    [key: string]: unknown;
    category: {
      [key: string]: unknown;
      id: number;
      name: string;
    };
  };
}

export interface ITaskUsedResourceResponseItem {
  [key: string]: unknown;
  id: number;
  resource: {
    [key: string]: unknown;
    cultureVariation: ICultureVariation | null;
    name: string;
    recommendedNorm: number;
    id: number;
    type: {
      id: number;
      name: string;
      unit: string;
      seed: boolean;
    };
  };
  company: {
    id: number;
    name: string;
    companyCode: string;
    city: string;
    accepted: boolean;
  };
  amount: number;
  fieldTask: {
    [key: string]: unknown;
    fieldSeason: {
      [key: string]: unknown;
      field: string;
      area: number;
    };
    id: number;
    progress: number;
  };
}

export interface ITaskResourceGroupedByCompanyItem {
  company: {
    id: number;
    name: string;
  };
  resources: {
    resourceId: number;
    name: string;
    totalAmount: number;
  }[];
}

export interface ITaskPlannedResource {
  globalResource?: ITaskAvailableResource;
  id?: number;
  name: string;
  cultureName?: string;
  cultureId?: number;
  norm: number;
  recommendedNorm: number;
  resourceId: number;

  price?: number;
}
export interface ITaskUsedResource extends ITaskPlannedResource {
  company?: {
    id: number;
    name: string;
    companyCode: string;
    city: string;
    accepted: boolean;
  };
  amount?: number;
  area?: number;
  fieldName?: string;
  fieldTaskId?: number;
}

export interface ITaskAvailableResource {
  id: number;
  name: string;
  countryCode: string;
  type: {
    id: number;
    name: string;
    unit: string;
    seed: boolean;
    yield: boolean;
  };
  recommendedNorm: number;
  cultureVariation: ICultureVariation | null;

  n: null | number;
  p: null | number;
  k: null | number;
  s: null | number;
  description: null | string;
}

export interface ITaskAvailableResourceChilden {
  value: number;
  label: string;
  resource: ITaskAvailableResource;
  disabled?: boolean;
  children?: {
    value: number;
    label: string;
    resource: ITaskAvailableResource;
    disabled?: boolean;
  }[];
}

export interface ITaskAvailableResourceOption {
  value: number;
  label: string;
  children: ITaskAvailableResourceChilden[];
}

export enum AddTaskResourceFormEnum {
  resourceTypeIdAndResourceId = 'resourceTypeIdAndResourceId',
  norm = 'norm',
}

export type TaskListTabType = 'tasks' | 'runsWithoutTask';

export interface IRunResponseProperties {
  id: number;
  datetime_from: string;
  datetime_to: string;
  gps_vehicle_id: number;
  field_season_id: number | null;
  next_field_season_id: number | null;
  beacon_id: string | null;
  slim_id: string | null;
  implement_width: number | null;
  type: 'ROAD' | string;
  distance: number;
  area_covered: number | null;
  total_area: number | null;
  field_name: string | null;
  vehicle_model: string | null;
  culture_id: number | null;
  culture_name: string | null;

  area?: number;
  season_id?: number;
  field_id?: number;
  field_geometry_id?: string;
  company_id?: number;
  company_name?: string;
  culture_color?: string | null;
  duration: number | null;
  culture_variation_id: number | null;
  culture_variation_name: string | null;
}

export interface ITransportOfRoutesWithoutTask {
  gpsTransport: IGpsTransport;
  model: string | null;
}

export interface IRouteItem {
  id: number;
  routeFeature: {
    geometry: {
      coordinates: FieldsBoundaries;
      type: 'MultiPoint' | 'LineString';
    };
    type: string;
    geometry_name: string;
    properties: IRunResponseProperties;
  };
  fieldFeature?: IFieldFeature;
  transportColor?: string;
  timeSpent: string;
  fieldSeasonId?: number;
}
export interface IFieldRoutes {
  fieldSeasonId: number | undefined;
  fieldFeature: IFieldFeature | undefined;
  routes: IRouteItem[];
}

export interface IRoutesWithoutTaskState {
  routes: IRouteItem[];
  fields: IFieldRoutes[];
  transport?: ITransportOfRoutesWithoutTask[];
}

export interface IRoutesWithoutTaskFilters {
  startDate?: string;
  endDate?: string;
  gpsVehicleIds?: number[];
  cultureIds?: number[];
  durationFrom?: number;
  durationTo?: number;
}
export interface IChangedFormField<T> {
  touched?: boolean;
  validating?: boolean;
  errors?: string[];
  name: NamePath;
  value?: T;
}

export type RoutesTableItemType = {
  id: number;
  routeIds: number[];
  gpsTransportId: number;
  model: string | null;
  beaconId: string | null;
  slimId: string | null;
  startDate: string | null;
  endDate: string | null;
  distance: number | null;
  fieldSeasonIds: (number | null)[];
  area: string | null;
};

export interface IRouteWithSelectedImplement {
  routeId: number;
  implementId?: number;
  employeeId?: number;
}

export type CreateTaskWithRoutesPayload = {
  routeId: number;
  implementId: number;
  completedArea: number;
  fieldId: number;
  beaconId?: string;
  employeeId?: number;
  slimId?: string;
}[];

export type GpsVehicleData = {
  id: number;
  gpsId: number;
  companyId: number;
  garageVehicleId: number;
  licensePlate: string;
  color: string;
  createdAt: string;
  updatedAt: string;
  assigned: boolean;
};

export type RouteData = {
  id: number;
  from: string;
  to: string;
  gpsVehicle: GpsVehicleData;

  fieldSeasonId: number;
  nextFieldSeasonId: null | number;
  beaconId: string | null;
  slimId: null | string;
  implementWidth: null | number;
  type: 'FIELD' | 'IDLE';
  distance: number;
  fuelConsumedFrom: number;
  fuelConsumedTo: number;
  isMarkedForDeletion: null | boolean;
  createdAt: null | string;
  companyId: number;
  fieldTaskId: null | number;
  geometry: {
    // TODO types
    coordinates: any;
  };
};

export type RouteWithField = {
  route: RouteData;
  field?: IFieldFeature;
};

export type RoutesWithField = {
  routes: RouteData[];
  field?: IFieldFeature;
};
